// TOEFL iBT Mock 4 — Listening (2026 format, multistage adaptive, advanced)
//
// Task types:
//   response     – Listen and Choose a Response: hear one line, pick the best reply.
//   conversation – Listen to a Conversation (campus / everyday life).
//   announcement – Listen to an Announcement (campus or public notice).
//   talk         – Listen to an Academic Talk (short lecture).
//
// Like Mocks 5–7, this set leans on indirect replies, implied meaning,
// speaker attitude and "why does the speaker mention…" questions, and wrong
// options often reuse words from the audio to punish word-matching.
//
// Each recording plays once. Questions appear after it finishes.
// gender ("m" / "f") picks the synthesized voice for each line.

export const MODULE_SECONDS = { module1: 15 * 60, module2: 12 * 60 };

export const TASK_LABELS = {
  response: "Choose a Response",
  conversation: "Conversation",
  announcement: "Announcement",
  talk: "Academic Talk",
};

const W = (text) => ({ speaker: "Woman", gender: "f", text });
const M = (text) => ({ speaker: "Man", gender: "m", text });

const response = (id, line, options, correct) => ({
  id,
  type: "response",
  lines: [line],
  questions: [{ question: "Choose the best response.", options, correct }],
});

export const listeningModules = {
  module1: [
    response("m1-r1", M("You wouldn't mind swapping lab partners for the next experiment, would you?"), [
      "I wouldn't mind a new lab coat.",
      "Not if Dr. Chen agrees to it.",
      "We swapped our notes last week.",
      "The next experiment is on Tuesday.",
    ], 1),
    response("m1-r2", W("I heard the dining hall is switching to a new food supplier."), [
      "Let's hope the new one has more vegetarian options.",
      "I switched my meal plan last month.",
      "The dining hall is next to the library.",
      "I heard it was a long hall.",
    ], 0),
    response("m1-r3", M("Shouldn't that article be cited in your bibliography?"), [
      "The article was about cities.",
      "My bibliography is two pages long.",
      "I should read more articles.",
      "Good catch. I'll add it right away.",
    ], 3),
    response("m1-r4", W("I could use a hand moving these chairs into the seminar room."), [
      "I moved into a new room last week.",
      "Sure, how many do you need in there?",
      "My hands are cold too.",
      "The seminar is about furniture design.",
    ], 1),
    response("m1-r5", M("I'd ask Professor Ito for a recommendation, but she hardly knows me."), [
      "I hardly know her either.",
      "She recommended a good book to me.",
      "Why not visit her office hours a few times first?",
      "I asked her a question in class.",
    ], 2),
    response("m1-r6", W("Weren't you going to drop the economics course?"), [
      "I dropped my books on the way to class.",
      "I changed my mind after the first tutorial.",
      "Economics is a popular course.",
      "Yes, I was going to the course.",
    ], 1),
    response("m1-r7", M("This is the last time I leave my assignment until the night before."), [
      "I was up late last night too.",
      "The assignment was last week.",
      "This is the last assignment of the term.",
      "Didn't you say that last month?",
    ], 3),
    response("m1-r8", W("There's no point in going to the career talk if it's just about resumes."), [
      "Actually, I heard they'll cover interview skills too.",
      "The point of the talk is at the end.",
      "I wrote my resume last year.",
      "There's no career center on campus.",
    ], 0),
    {
      id: "m1-conv",
      type: "conversation",
      title: "Special Collections Library",
      lines: [
        W("Hi, I'm writing a paper on nineteenth-century travel diaries, and the catalog says you have one written by a woman who sailed to South America in eighteen sixty."),
        M("Yes, the Harriet Lowell diary. It's one of our most requested items. Have you handled original manuscripts before?"),
        W("No, this would be my first time."),
        M("Then there are a few rules. You'll need to book a time slot, because it can only be viewed in our reading room, and we ask that you use pencils only, no pens. You'll also wear cotton gloves when turning the pages."),
        W("Is there any chance I could just get a scanned copy instead? That would be easier."),
        M("Most of it has been digitized, actually, and it's available online. But the last twenty pages were water-damaged, and they haven't been scanned yet. If you need those pages, you'd have to see the original."),
        W("Those are the pages from the return journey, aren't they? That's exactly the part I'm writing about."),
        M("Then I'd book soon. Slots for that diary usually fill up two weeks in advance."),
      ],
      questions: [
        {
          question: "Why does the woman go to the library?",
          options: [
            "To return a damaged book",
            "To get permission to borrow a rare manuscript",
            "To arrange access to a historical diary",
            "To ask for help writing a travel guide",
          ],
          correct: 2,
        },
        {
          question: "Why can't the woman use only the online version?",
          options: [
            "The pages she needs have not been digitized.",
            "The online version is only available to professors.",
            "The scans are too blurry to read.",
            "The website requires a booking two weeks in advance.",
          ],
          correct: 0,
        },
        {
          question: "What does the man imply when he says, \"Then I'd book soon\"?",
          options: [
            "The library will close for repairs soon.",
            "The diary may be sent to another library.",
            "She needs to finish her paper within two weeks.",
            "She may not get a slot if she waits.",
          ],
          correct: 3,
        },
      ],
    },
    {
      id: "m1-ann",
      type: "announcement",
      title: "Guest Lecture Change",
      lines: [
        M("Attention, please. Tonight's guest lecture by the novelist Amara Diallo has been moved from the Humanities Building to the main auditorium in the Performing Arts Center, because far more people registered than expected. The start time remains seven o'clock, but doors will now open at six-fifteen. Registered guests should bring the confirmation email on their phones; there's no need to print it. A limited number of walk-in seats will be available from six forty-five, but we can't guarantee entry to anyone without registration. The book signing will take place in the lobby after the talk rather than in the auditorium itself."),
      ],
      questions: [
        {
          question: "Why has the lecture been moved?",
          options: [
            "The Humanities Building is being repaired.",
            "The number of registrations was higher than expected.",
            "The speaker requested a different room.",
            "The original room had a scheduling conflict.",
          ],
          correct: 1,
        },
        {
          question: "What can be inferred about people who have not registered?",
          options: [
            "They will be able to attend only the book signing.",
            "They must print a confirmation email.",
            "They might not be able to get into the lecture.",
            "They should arrive at six-fifteen to register.",
          ],
          correct: 2,
        },
      ],
    },
    {
      id: "m1-talk",
      type: "talk",
      title: "Neuroscience: Sleep and Memory",
      lines: [
        W("For a long time, sleep was thought of as a passive state, a kind of shutdown that lets the body rest. We now know that the brain is extremely busy during sleep, and one of its most important jobs seems to be strengthening memories. During the day, new experiences are first stored in a region called the hippocampus, which acts a bit like a temporary notebook. But the hippocampus has limited capacity. During deep sleep, the brain appears to replay recent experiences, and through this replay, the information is gradually transferred to the cortex, where long-term memories are stored. Researchers have actually observed this in rats. As a rat runs through a maze, particular neurons in its hippocampus fire in a specific sequence. Later, while the rat sleeps, the same sequence fires again, but several times faster. In human studies, people who learned a list of word pairs and then slept recalled significantly more the next day than people who learned the same list in the morning and stayed awake for the same number of hours. So the common student habit of staying up all night before an exam may be self-defeating: it reduces the very process that turns studying into lasting knowledge."),
      ],
      questions: [
        {
          question: "What is the talk mainly about?",
          options: [
            "Why rats are useful in brain research",
            "The role of sleep in strengthening memories",
            "How to fall asleep more quickly",
            "Differences between human and animal brains",
          ],
          correct: 1,
        },
        {
          question: "Why does the professor compare the hippocampus to a temporary notebook?",
          options: [
            "To explain that it stores new memories for a limited time",
            "To show that it controls reading and writing",
            "To argue that it is the most important part of the brain",
            "To explain why people forget their dreams",
          ],
          correct: 0,
        },
        {
          question: "What did researchers observe in sleeping rats?",
          options: [
            "Their neurons stopped firing completely.",
            "They moved as if they were running a maze.",
            "Neuron sequences from the maze were repeated at a faster speed.",
            "Their hippocampus became larger.",
          ],
          correct: 2,
        },
        {
          question: "What is the professor's attitude toward staying up all night before an exam?",
          options: [
            "She thinks it helps students who are well prepared.",
            "She believes it has no effect on memory.",
            "She recommends it only for memorizing word lists.",
            "She considers it counterproductive.",
          ],
          correct: 3,
        },
      ],
    },
  ],

  // ── Harder Module 2 ──────────────────────────────────────────
  upper: [
    response("up-r1", W("I'd hardly describe the results as conclusive."), [
      "I'd describe them in the conclusion.",
      "Agreed. The sample was far too small.",
      "The results came out on Monday.",
      "Hardly anyone attended the talk.",
    ], 1),
    response("up-r2", M("You'd think they'd have fixed the heating by now."), [
      "Tell me about it. I'm wearing two sweaters.",
      "I think it's fixed to the wall.",
      "They'd have to heat the food first.",
      "Now is the best time to think.",
    ], 0),
    response("up-r3", W("Not that it's any of my business, but are you sure about changing your major?"), [
      "My business major is going well.",
      "It's none of your business where I live.",
      "I'm sure the major is popular.",
      "I've thought about it for months, actually.",
    ], 3),
    response("up-r4", M("For someone who claims to hate public speaking, you did remarkably well."), [
      "I claimed my prize after the event.",
      "I hate it when people speak publicly.",
      "I practiced in front of a mirror for days.",
      "Public transport is remarkable here.",
    ], 2),
    response("up-r5", W("If it weren't for your notes, I'd never have passed that course."), [
      "I passed the course notes to Ana.",
      "I'm just glad they were useful.",
      "It weren't for me either.",
      "The course was never offered.",
    ], 1),
    {
      id: "up-conv",
      type: "conversation",
      title: "Changing Thesis Advisors",
      lines: [
        M("Professor Banerjee, thanks for meeting me. I'm in a slightly awkward situation, and I'd value your advice as graduate director."),
        W("Of course, David. What's going on?"),
        M("My thesis has shifted quite a bit. It started as a study of urban water policy, but it's turned into something much more about public health, and my advisor, Professor Kim, has said himself that it's moving outside his expertise."),
        W("That happens more often than people think. Have you two talked about bringing in a co-advisor rather than switching completely?"),
        M("No. I assumed I'd have to choose one or the other."),
        W("Not at all. A co-advisor from the School of Public Health would give you the expertise you need, and you wouldn't lose Professor Kim's knowledge of the policy side, or the two years of work you've done with him. It's also much less awkward than leaving."),
        M("That sounds much better. Should I approach the public health faculty myself?"),
        W("I'd talk to Professor Kim first. If he suggests a name, the request carries more weight, and it shows you're not going behind his back."),
      ],
      questions: [
        {
          question: "Why does the student want to talk to the graduate director?",
          options: [
            "His advisor is leaving the university.",
            "His thesis topic has moved beyond his advisor's area of expertise.",
            "He wants to change from public health to urban policy.",
            "He has not made progress in two years.",
          ],
          correct: 1,
        },
        {
          question: "What does the director suggest?",
          options: [
            "Adding a second advisor",
            "Changing the thesis topic back",
            "Transferring to the School of Public Health",
            "Postponing the thesis for a year",
          ],
          correct: 0,
        },
        {
          question: "Why does the director advise the student to speak to Professor Kim first?",
          options: [
            "Professor Kim must approve all thesis topics.",
            "The public health faculty do not accept direct requests.",
            "Professor Kim is the graduate director's supervisor.",
            "It would strengthen the request and avoid seeming disloyal.",
          ],
          correct: 3,
        },
      ],
    },
    {
      id: "up-talk",
      type: "talk",
      title: "Anthropology: The Puzzle of Early Farming",
      lines: [
        M("We usually think of the invention of farming as obvious progress. But the archaeological evidence presents a puzzle. When we compare skeletons of hunter-gatherers with those of the earliest farmers in the same regions, the farmers often look worse off. They tended to be shorter, they show more signs of malnutrition in their bones and teeth, and they suffered more from infectious diseases, which spread easily in crowded, permanent settlements with domesticated animals nearby. Farming was also, in many cases, more work. So why did people adopt it? One explanation focuses on population. Farming produces far more calories per acre than foraging, even if those calories are less nutritious. A farming community could therefore support many more people, and over generations, larger farming populations simply outnumbered and displaced neighboring foragers. Another explanation is that the shift was gradual and nobody chose it consciously. Groups that began by tending wild cereals a little more each year became more dependent on them, and by the time the costs were apparent, there was no realistic way back. Notice that both explanations accept the same uncomfortable idea: farming may have spread not because it made individual lives better, but because it allowed societies to grow."),
      ],
      questions: [
        {
          question: "What puzzle does the professor discuss?",
          options: [
            "Why hunter-gatherers lived in crowded settlements",
            "Why farming spread although early farmers were often less healthy",
            "Why the first farmers chose to grow cereals",
            "Why skeletons of early farmers are rarely found",
          ],
          correct: 1,
        },
        {
          question: "According to the professor, why did diseases spread more easily among farmers?",
          options: [
            "They lived close together and near animals in permanent settlements.",
            "They traveled long distances to trade.",
            "They ate wild plants that carried infections.",
            "They had less access to clean water than foragers.",
          ],
          correct: 0,
        },
        {
          question: "Why does the professor mention that farming produces more calories per acre?",
          options: [
            "To show that farmers' diets were more nutritious",
            "To explain why farming required less work",
            "To argue that foragers were often starving",
            "To explain how farming could support larger populations",
          ],
          correct: 3,
        },
        {
          question: "What do the two explanations have in common?",
          options: [
            "Both claim that people chose farming deliberately.",
            "Both depend on evidence from teeth.",
            "Both suggest farming spread without improving individual well-being.",
            "Both argue that foragers adopted farming peacefully.",
          ],
          correct: 2,
        },
      ],
    },
  ],

  // ── Easier Module 2 ──────────────────────────────────────────
  lower: [
    response("low-r1", M("Is there a pharmacy near here?"), [
      "I need some medicine.",
      "There's one across the street.",
      "Yes, I'm near here.",
      "The pharmacy was busy.",
    ], 1),
    response("low-r2", W("What are you doing this weekend?"), [
      "I'm visiting my grandparents.",
      "I did it last weekend.",
      "The weekend is two days.",
      "It's on Saturday.",
    ], 0),
    response("low-r3", M("Can you help me with this math problem?"), [
      "Math is my favorite subject.",
      "I had a problem with my car.",
      "Sure, which one is it?",
      "It's on page ten.",
    ], 2),
    response("low-r4", W("Did you lock the door when you left?"), [
      "I left at noon.",
      "The door is brown.",
      "I lost my key.",
      "Yes, I'm sure I did.",
    ], 3),
    response("low-r5", M("How much does a coffee cost here?"), [
      "I'd like a large one.",
      "About three dollars.",
      "Coffee is very popular.",
      "It's very hot.",
    ], 1),
    {
      id: "low-ann",
      type: "announcement",
      title: "Lost and Found Announcement",
      lines: [
        W("Hello, students. The campus lost and found office has moved. It is now in Room 104 of the Student Services Building, next to the main entrance. The office is open from nine a.m. to four p.m., Monday through Friday. If you have lost something, please bring your student ID when you come to collect it. Items that are not collected within thirty days will be donated to charity."),
      ],
      questions: [
        {
          question: "What is the announcement mainly about?",
          options: [
            "A new charity on campus",
            "Changes to the lost and found office",
            "New student ID cards",
            "The hours of the main entrance",
          ],
          correct: 1,
        },
        {
          question: "What happens to items that are not collected within thirty days?",
          options: [
            "They are given to charity.",
            "They are sold to students.",
            "They are moved to Room 104.",
            "They are thrown away.",
          ],
          correct: 0,
        },
      ],
    },
    {
      id: "low-talk",
      type: "talk",
      title: "Botany: Why Leaves Change Color",
      lines: [
        M("Every autumn, the leaves of many trees turn yellow, orange, and red. Why does this happen? During spring and summer, leaves are full of a green substance called chlorophyll. Chlorophyll helps the tree use sunlight to make food. It is so green that it hides other colors in the leaf. In autumn, the days get shorter and cooler, and the tree stops making chlorophyll. As the green color disappears, the yellow and orange colors that were there all along become visible. Some trees, like maples, also make new red colors in autumn. Bright, sunny days and cool nights make these red colors stronger, which is why some years have more colorful autumns than others."),
      ],
      questions: [
        {
          question: "What does chlorophyll do?",
          options: [
            "It makes leaves fall from trees.",
            "It protects trees from cold weather.",
            "It turns leaves red.",
            "It helps trees make food from sunlight.",
          ],
          correct: 3,
        },
        {
          question: "Why do leaves look yellow and orange in autumn?",
          options: [
            "The tree paints them with new colors.",
            "Rain washes the green color away.",
            "The green color disappears and shows colors that were already there.",
            "The leaves absorb colors from the soil.",
          ],
          correct: 2,
        },
        {
          question: "Which trees make new red colors in autumn?",
          options: [
            "Some trees, like maples",
            "All trees",
            "Only very old trees",
            "Trees that grow near water",
          ],
          correct: 0,
        },
        {
          question: "What makes red autumn colors stronger?",
          options: [
            "Rainy days and warm nights",
            "Sunny days and cool nights",
            "Long summers",
            "Strong winds",
          ],
          correct: 1,
        },
      ],
    },
  ],
};
